"use client";
import React from "react";
import formatMsg from "../../util/formatMsg";

// Badge entry from a full subscription (the parent's own list, which carries holds and dunning
// state the booking-map omits). Split the way the Autopay tab splits them: a consent hold is
// "awaiting approval", not a failure, and a card-less enrolment is "pending card" even though the
// backend flags it needsAttention too — each has a different fix, so each gets its own badge.
export function autopayEntryFromSubscription(sub?: any) {
  if (!sub) return undefined;
  const holds = sub.autopayHolds && typeof sub.autopayHolds === "object" ? Object.keys(sub.autopayHolds) : [];
  const awaitingApproval = holds.length > 0;
  const retrying = (Number(sub.retryState?.failCount) || 0) > 0 && sub.status === "active";
  return {
    status: sub.status,
    autopayEnrolled: !!sub.autopayEnrolled,
    awaitingApproval,
    needsAttention: !awaitingApproval && sub.status !== "pending_setup" && (!!sub.needsAttention || retrying),
  };
}

// Compact autopay indicator for list rows (invoices / bookings). `entry` is a bookingAutopayMap
// value: { status, autopayEnrolled, needsAttention, awaitingApproval? }. Renders nothing when autopay was never set
// up (or was cancelled), so non-recurring rows stay clean. Styling follows the recurring-ux tokens.
//
// `style` is merged last, for callers in tighter layouts than a table row — the mobile invoice
// card sits in a column capped at 162px on small phones, where nowrap would overflow.
export default function AutopayListBadge({ entry, style }: { entry?: any; style?: React.CSSProperties }) {
  if (!entry || !entry.autopayEnrolled || entry.status === "cancelled") return null;

  let bg = "#F0FAF5", fg = "#1F9D6B", line = "#BFE7D4", label = formatMsg("autopay");
  if (entry.awaitingApproval) {
    bg = "#FBF3E6"; fg = "#B7791F"; line = "#F0D9AE";
    label = formatMsg("autopay") + " · " + formatMsg("autopay.awaitingApproval");
  } else if (entry.needsAttention) {
    bg = "#FDECEC"; fg = "#D64545"; line = "#F3C8C8";
    label = formatMsg("autopay") + " · " + formatMsg("autopay.needsAttention");
  } else if (entry.status === "completed") {
    bg = "#F2F2F7"; fg = "#6B7280"; line = "#E5E7EB";
    label = formatMsg("autopay") + " · " + formatMsg("autopay.status.completed");
  } else if (entry.status === "pending_setup") {
    bg = "#FBF3E6"; fg = "#B7791F"; line = "#F0D9AE";
    label = formatMsg("autopay") + " · " + formatMsg("autopay.pendingCard");
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: bg, color: fg, border: `1px solid ${line}`, borderRadius: 999, padding: "1px 8px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", marginTop: 4, ...(style || {}) }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: fg }} />
      {label}
    </span>
  );
}
